<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <div>
        <ClientListFilterSection
            :filter-controller="filterController"
            :mobile-show-filterbar="showMobileFilterbar"
        />
        <ClientListTableSection
            :selected-account="selectedAccount"
            :rich-data-table-controller="richDataTableController"
            :filter-controller="filterController"
            :is-selected-account-paid="isSelectedAccountPaid"
            :mobile-show-filterbar="showMobileFilterbar"
            @updateMobileShowFilterbar="showMobileFilterbar = $event"
        />
    </div>
</template>

<script>
import ClientModule from "../../../store/modules/client";
import BookmarkModule from "../../../store/modules/bookmark";
import AppModule from "../../../store/modules/app";
import AccountModule from "../../../store/modules/account";
import ClientGroupModule from "../../../store/modules/client_groups";
import ContractModule from "../../../store/modules/contract";
import {computed, onMounted, reactive, ref,} from "@vue/composition-api";
import errorDialog from "@/views/ErrorDialog/errorDialog"

import {getCookie} from "@/helpers/helpers";
import FilterBarController from "@/components/ModernFilterBar/FilterBarController";
import UrlAdapter from "@/urlAdapter/urlAdapter";
import RichDataTableController from "@/components/RichDataTable/RichDataTableController";
import CustomFieldsModule from "../../../store/modules/custom_fields";
import ClientListFilterSection from "./ClientListFilterSection";
import ClientListTableSection from "./ClientListTableSection";
import {get} from "@/helpers/get-set";
import {getCustomFieldsByRelatedType} from "../../../composables/use-custom-fields";
import {getHeaders} from "../../../components/RichDataTable/richDataTableComponents";
import {useMemberStore} from "../../../store/modules/member";


export default {
    name: "ClientList",
    components: {
        ClientListTableSection,
        ClientListFilterSection,
    },
    setup() {
        const appModule = AppModule()
        const bookmarkModule = BookmarkModule()
        const contractModule = ContractModule()
        const clientModule = ClientModule()
        const accountModule = AccountModule()
        const clientGroupModule = ClientGroupModule()
        const customFieldsModule = CustomFieldsModule()
        const memberStore = useMemberStore()

        const showMobileFilterbar = ref(true)
        const default_account_id = getCookie("account_id");

        const urlAdapter = new UrlAdapter()
        urlAdapter.loadDefaultBookmark('client', bookmarkModule.bookmarks.value)

        /** @type {FilterBarController} */
        const filterController = reactive(FilterBarController.init(urlAdapter))
        /** @type {RichDataTableController} */
        const richDataTableController = reactive(RichDataTableController.initAsync(urlAdapter))
        const options = [
            {key: "sortBy", default_value: "name"},
            {key: "sortDesc", default_value: false},
            {key: "itemsPerPage", default_value: 50},
            {key: "page", default_value: 1}
        ]
        richDataTableController.setDefaultOptions(options)

        urlAdapter.addGetStateCallback(() => {
            return {
                active_bookmark_id: urlAdapter.getUrlParameter('active_bookmark_id')
            }
        })

        const selectedAccount = computed(() => {
            const selected_account_id_list = filterController.getValue('account_id') || urlAdapter.getUrlParameter('account_id')
            const id = selected_account_id_list ? parseInt(selected_account_id_list[0]) : null

            if (accountModule.available_account_for_clients.value.find(x => x.id === id)) {
                return accountModule.available_account_for_clients.value.find(x => x.id === id)
            } else if (accountModule.available_account_for_clients.value.find(x => x.id === default_account_id)) {
                return accountModule.available_account_for_clients.value.find(x => x.id === default_account_id)
            }

            return accountModule.available_account_for_clients.value[0]
        });

        const getMembers = computed(() => {
            return memberStore.getMembersByCompany(selectedAccount.value.id)
        })

        const getCustomFields = () => {
            return getCustomFieldsByRelatedType(selectedAccount.value.id, ["client"])
        }

        const getClientGroups = computed(() => {
            return clientGroupModule.client_groups_by_account(selectedAccount.value.id).value
        })

        const billingEnabled = computed(() => {
            if (!get(selectedAccount, 'value')) return false;
            return accountModule.isFeatureEnabled(get(selectedAccount, 'value.id'), "billing", false);
        });

        const monitoringEnabled = computed(() => {
            if (!get(selectedAccount, 'value')) return false;
            return accountModule.isFeatureEnabled(get(selectedAccount, 'value.id'), "monitoring", false);
        });

        const clientPortalEnabled = computed(() => {
            if (!get(selectedAccount, 'value')) return false;
            return accountModule.isFeatureEnabled(get(selectedAccount, 'value.id'), "client_portal", false);
        });

        const integrationEnabled = computed(() => {
            return accountModule.isAnyIntegrationEnabled(get(selectedAccount, 'value.id'))
        })

        const budgetsEnabled = computed(() => {
            return accountModule.isFeatureEnabled(get(selectedAccount, 'value.id'), "budget", false)
        })

        const isSelectedAccountPaid = computed(() => {
            return accountModule.isBundle(selectedAccount.value.id, false) || selectedAccount.value.type === "person"
        })

        const filters = computed(() => {
            const accountId = selectedAccount.value.id
            const availableAccountsForClients = accountModule.available_account_for_clients
            const contractTemplates = contractModule.contractTemplates

            let filters = [
                {key: "account_id", default_value: [accountId], items: availableAccountsForClients, default: true},
                {key: "default_person_id_list", default_value: [], items: getMembers},
                {key: "person_id_list", default_value: [], items: getMembers, default: true},
                {key: "client_search", default_value: [""], default: true},
                {key: "contact_search", default_value: [""], default: true},
                {key: "client_group_id_list", default_value: [], items: getClientGroups},
                {key: "client_status", default_value: ["active"], default: true},
                {key: "client_last_import", default_value: ["1"]},
                {key: "person_ids_from_custom_fields", default_value: [], items: getMembers},
                {
                    key: "custom_field",
                    default_value: [],
                    items: getCustomFields,
                    members: getMembers,
                    repeatable: true
                },
            ]

            if (billingEnabled) {
                filters.push({key: "is_billable", default_value: ["1"]})
                filters.push({key: "contract_template_id", default_value: [""], items: contractTemplates})
                filters.push({key: "contract_status", default_value: ["active"]})
            }
            if (monitoringEnabled) {
                filters.push({key: "is_monitored", default_value: ["1"]})
            }

            return filters
        })

        const afterFilterInput = function (filter_key, value) {
            if (['account_id'].includes(filter_key)) {
                fetchData(value[0].id)
            }
        }

        filterController.setAfterFilterInputFunc((...a) => afterFilterInput(...a))
        filterController.setBeforeFilterInputFunc((...a) => filterController.resetFiltersOnAccountChange(...a))

        const setHeaders = () => {
            const config = {
                billing: billingEnabled.value,
                monitoring: monitoringEnabled.value,
                client_portal: clientPortalEnabled.value,
                integration: integrationEnabled.value,
                budgets: budgetsEnabled.value
            }
            const accountId = selectedAccount.value.id
            const headers = getHeaders(accountId, "client", config)
            richDataTableController.setAllHeaders(headers)

            const columns = urlAdapter.getUrlParameter('columns')
            if (!columns || columns.length === 0) {
                const defaultHeaders = headers.filter(x => x.default)
                const sortedDefaultHeaders = defaultHeaders.sort((a, b) => {
                    if (a.defaultSortOrder < b.defaultSortOrder) return -1
                    if (a.defaultSortOrder > b.defaultSortOrder) return 1
                    return 0
                })
                const visibleHeaders = sortedDefaultHeaders.map(x => x.value)
                richDataTableController.setVisibleHeaders(visibleHeaders)
            } else {
                richDataTableController.setVisibleHeaders(columns)
            }
        }

        const backendQuery = async () => {
            const columns = richDataTableController.getVisibleHeaders().map(x => x.value)
            const resp = await clientModule.fetchClients({
                ...filterController.getCurrentFilterValues(),
                ...richDataTableController.getOptions(),
                columns: columns,
            })
            richDataTableController.setRows(resp.clients, resp.total)

            let list_cache = {
                'query': urlAdapter.getFullQuery(),
                'return_path': urlAdapter.getFullPath()
            }
            localStorage.setItem("cached_list", JSON.stringify(list_cache));
        };

        const fetchData = async (accountId) => {
            await appModule.fetchClassifiers({account_id: accountId, force: {default: true, types: ['custom_fields']}})

            await contractModule.fetchContractTemplates({account_id: accountId})

            await customFieldsModule.fetchCustomFields(accountId, 'task', 0, false).catch(ex => errorDialog.showError(ex));
        }

        onMounted(async () => {
            filterController.initFilters(filters.value)

            if (urlAdapter) {
                richDataTableController.loadUrlState()
            }

            filterController.startUrlWatcher()
            await fetchData(selectedAccount.value.id)

            setHeaders();
            richDataTableController.setLoadDataFunc(backendQuery)
            await richDataTableController.loadDataFunc()
        })

        return {
            afterFilterInput,
            /** @type {FilterBarController} */
            filterController,
            /** @type {RichDataTableController} */
            richDataTableController,
            urlAdapter,
            selectedAccount,
            isSelectedAccountPaid,
            showMobileFilterbar,
            billingEnabled,
            monitoringEnabled
        };
    }
};
</script>
